"use client";

import { useState } from "react";

import { Icon, Image, ImageSizes, Text, View } from "@/components";
import cn from "@/utils/cn";

import type { AvatarProps } from "@/components/types";
import type { JSX } from "react";

/**
 * @name Avatar
 * @description Renders a circular avatar image.
 *
 * @param {AvatarProps} props - Component props.
 * @param {string} [props.src] - Image source URL.
 * @param {string} [props.alt="Avatar"] - Alt text.
 * @param {number} [props.size=40] - Size in pixels.
 * @param {string} [props.className] - Additional CSS classes.
 * @returns {JSX.Element} The rendered avatar.
 */
const Avatar = ({
  name,
  size = "1x",
  className,
  ...props
}: AvatarProps): JSX.Element => {
  const s = ImageSizes[size] * 2;
  const [imageError, setImageError] = useState<boolean>(false);
  const getInitials = (name: string) =>
    name
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const renderContent = () => {
    if (props.src && !imageError) {
      return (
        <Image
          width={s}
          height={s}
          src={props.src}
          loading="eager"
          sizes={`${s}px`}
          className="object-cover"
          alt={props.alt ?? "Avatar"}
          onError={() => setImageError(true)}
          {...props}
        />
      );
    }

    if (name) {
      return (
        <Text className="text-gray-600 dark:text-gray-300 font-bold text-sm">
          {getInitials(name)}
        </Text>
      );
    }

    return <Icon name="user" className="text-gray-400" />;
  };

  return (
    <View
      className={cn(
        className,
        "relative rounded-full overflow-hidden shrink-0 border flex items-center justify-center"
      )}
      style={{ width: s, height: s }}
    >
      {renderContent()}
    </View>
  );
};

export default Avatar;
